import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/app/AppShell';
import {
  usePayrollRuns,
  usePayrollRun,
  useCalculateJpRun,
  useCalculateJpBonus,
  useCalculateUsRun,
  useCalculateUkRun,
  useCalculateDeRun,
  usePayrollSummary,
  usePayrollAttendanceSummary,
  useMarkRunPaid,
  useCloseRun,
  payslipPdfUrl,
  type PayrollLine,
  type PayrollRun,
} from '@/lib/payroll';
import { useCreateApproval } from '@/lib/approvals';
import { useMembers } from '@/lib/members';
import { useEmployees } from '@/lib/employees';
import { useMyCompany } from '@/lib/companies';
import { useCurrentUser } from '@/lib/auth';
import { US_STATES, UK_COMMON_TAX_CODES } from '@/lib/us-states';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtCents = (cents: number | null | undefined, currency: string): string => {
  if (cents == null) return '—';
  const decimals = ['JPY', 'KRW'].includes(currency) ? 0 : 2;
  return new Intl.NumberFormat(currency === 'JPY' ? 'ja-JP' : 'en-US', {
    style: 'currency', currency, maximumFractionDigits: decimals,
  }).format(cents / 100);
};

type CalcKind = 'jp' | 'jp_bonus' | 'us' | 'uk' | 'de';

export function PayrollPage() {
  const { t } = useTranslation();
  const runs = usePayrollRuns();
  const company = useMyCompany();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [calcKind, setCalcKind] = useState<CalcKind | null>(null);
  const [calcWarnings, setCalcWarnings] = useState<string[]>([]);

  const selected = usePayrollRun(selectedRunId ?? undefined);

  const me = useCurrentUser();
  // Running payroll is an owner/admin/manager action (mirrors the backend
  // RolesGuard on /payroll/*/calculate); members + employees can view runs
  // but not trigger a calculation.
  const canRun = ['owner', 'admin', 'manager'].includes(me.data?.role ?? '');
  const countriesEmployed = company.data?.countries ?? [];
  const canJp = canRun && countriesEmployed.includes('JP');
  const canUs = canRun && countriesEmployed.includes('US');
  const canUk = canRun && countriesEmployed.includes('GB');
  const canDe = canRun && countriesEmployed.includes('DE');

  return (
    <AppShell>
      <div className="px-8 py-8 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="display text-3xl font-semibold tracking-tight text-ink-900 serif-jp">{t('payroll.title')}</h1>
            <p className="mt-1 text-sm text-ink-500">{t('payroll.subtitle')}</p>
            <Link to="/app/global-payroll" className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-accent-700 hover:text-accent-600">🌐 {t('payroll.globalView', { defaultValue: 'Global payroll view — FX-normalized cost across jurisdictions →' })}</Link>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canJp && <button onClick={() => setCalcKind('jp')} className="btn-primary">{t('payroll.calculateJp')}</button>}
            {canJp && <button onClick={() => setCalcKind('jp_bonus')} className="btn-secondary">{t('payroll.calculateBonus')}</button>}
            {canUs && <button onClick={() => setCalcKind('us')} className="btn-secondary">{t('payroll.calculateUs')}</button>}
            {canUk && <button onClick={() => setCalcKind('uk')} className="btn-secondary">{t('payroll.calculateUk')}</button>}
            {canDe && <button onClick={() => setCalcKind('de')} className="btn-secondary">{t('payroll.calculateDe')}</button>}
          </div>
        </div>

        {calcWarnings.length > 0 && (
          <div className="mt-4 rounded-lg border border-warn-500/40 bg-warn-50 px-4 py-3 flex items-start gap-3">
            <span className="text-warn-600 font-semibold text-sm mt-0.5">⚠</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-warn-700">{t('payroll.calcWarnings_title', { defaultValue: 'Calculation warnings' })}</p>
              <ul className="mt-1 space-y-0.5">
                {calcWarnings.map((w, i) => (
                  <li key={i} className="text-xs text-warn-600">{w}</li>
                ))}
              </ul>
            </div>
            <button onClick={() => setCalcWarnings([])} className="text-warn-500 hover:text-warn-700 text-xs font-semibold shrink-0">{t('common.dismiss', { defaultValue: 'Dismiss' })}</button>
          </div>
        )}

        <PayrollSummaryCard />

        {canJp && <AttendanceForPeriodCard />}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <section className="lg:col-span-1 rounded-2xl border border-ink-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50">
              <h2 className="text-sm font-semibold text-ink-700">{t('payroll.recentRuns')}</h2>
            </div>
            <ul className="divide-y divide-ink-100 max-h-[600px] overflow-y-auto">
              {runs.isLoading && <li className="p-4 text-sm text-ink-500">{t('payroll.loading')}</li>}
              {runs.data?.length === 0 && <li className="p-4 text-sm text-ink-500">{t('payroll.noRuns')}</li>}
              {(runs.data ?? []).map((r) => {
                const isSel = r.id === selectedRunId;
                return (
                  <li key={r.id}>
                    <button onClick={() => setSelectedRunId(r.id)} className={`block w-full text-start px-4 py-3 transition ${isSel ? 'bg-accent-50' : 'hover:bg-ink-50'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-ink-900">{r.countryCode} · {r.periodYear}/{String(r.periodMonth).padStart(2,'0')}</span>
                        <span className="text-[10px] uppercase font-semibold text-ink-500">{t(`status.${r.status}`, { defaultValue: r.status })}</span>
                      </div>
                      <p className="num text-xs text-ink-500 mt-0.5">{r.runType} · {r.totals?.lines_count ?? 0} · {fmtCents(r.totals?.gross_cents, currencyFor(r.countryCode))}</p>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          <section className="lg:col-span-2">
            {!selectedRunId && (
              <div className="rounded-2xl border-2 border-dashed border-ink-300 bg-white p-10 text-center">
                <h3 className="text-base font-semibold text-ink-900">{t('payroll.selectRun')}</h3>
                <p className="mt-2 text-sm text-ink-600 max-w-md mx-auto">{t('payroll.selectRunHint')}</p>
              </div>
            )}
            {selected.data && <RunDetail run={selected.data.run} lines={selected.data.lines} />}
          </section>
        </div>
      </div>

      {calcKind === 'jp'       && <CalcJpDialog onClose={() => setCalcKind(null)} onSuccess={(id, warnings) => { setSelectedRunId(id); setCalcKind(null); setCalcWarnings(warnings ?? []); }} />}
      {calcKind === 'jp_bonus' && <CalcJpBonusDialog onClose={() => setCalcKind(null)} onSuccess={(id, warnings) => { setSelectedRunId(id); setCalcKind(null); setCalcWarnings(warnings ?? []); }} />}
      {calcKind === 'us'       && <CalcUsDialog onClose={() => setCalcKind(null)} onSuccess={(id, warnings) => { setSelectedRunId(id); setCalcKind(null); setCalcWarnings(warnings ?? []); }} />}
      {calcKind === 'uk'       && <CalcUkDialog onClose={() => setCalcKind(null)} onSuccess={(id, warnings) => { setSelectedRunId(id); setCalcKind(null); setCalcWarnings(warnings ?? []); }} />}
      {calcKind === 'de'       && <CalcDeDialog onClose={() => setCalcKind(null)} onSuccess={(id, warnings) => { setSelectedRunId(id); setCalcKind(null); setCalcWarnings(warnings ?? []); }} />}

      <style>{`
        .btn-primary { display:inline-flex; align-items:center; padding:0.5rem 1rem; border-radius:0.5rem; background:#1A1AFF; color:white; font-size:0.875rem; font-weight:600; transition:background 0.15s; }
        .btn-primary:hover { background:#0F12D6; }
        .btn-secondary { display:inline-flex; align-items:center; padding:0.5rem 0.75rem; border-radius:0.5rem; background:white; border:1px solid #D1D5DB; color:#1F2937; font-size:0.8125rem; font-weight:600; transition:background 0.15s; }
        .btn-secondary:hover { background:#F3F4F6; }
      `}</style>
    </AppShell>
  );
}

function PayrollSummaryCard() {
  const { t } = useTranslation();
  const summary = usePayrollSummary();

  if (summary.isLoading) return null;
  if (!summary.data) return null;

  const s = summary.data;
  const ccy = s.reportingCurrency;

  return (
    <div className="mt-4 rounded-2xl border border-ink-200 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-3 border-b border-ink-100 bg-ink-50 flex items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink-700">{t('payroll.summary_title', { defaultValue: 'Global payroll total' })}</h2>
        <span className="text-[10px] text-ink-500 uppercase tracking-wide">{t('payroll.summary_reportingCcy', { defaultValue: 'Reporting currency' })}: <span className="font-semibold text-ink-700">{ccy}</span></span>
      </div>
      <div className="px-5 py-4">
        <div className="grid grid-cols-3 gap-4">
          <Stat label={t('payroll.stat_gross')} value={fmtCents(s.totals.grossInReportingCents, ccy)} />
          <Stat label={t('payroll.stat_net')} value={fmtCents(s.totals.netInReportingCents, ccy)} />
          <Stat label={t('payroll.stat_employerBurden')} value={fmtCents(s.totals.employerBurdenInReportingCents, ccy)} />
        </div>

        {s.breakdown.length > 1 && (
          <div className="mt-4 rounded-lg border border-ink-100 overflow-hidden">
            <table className="min-w-full text-xs">
              <thead className="bg-ink-50 text-ink-600">
                <tr>
                  <th className="text-start font-semibold px-3 py-2">{t('payroll.summary_col_currency', { defaultValue: 'Currency' })}</th>
                  <th className="text-start font-semibold px-3 py-2">{t('payroll.summary_col_country', { defaultValue: 'Country' })}</th>
                  <th className="text-end font-semibold px-3 py-2">{t('payroll.summary_col_headcount', { defaultValue: 'HC' })}</th>
                  <th className="text-end font-semibold px-3 py-2">{t('payroll.stat_gross')}</th>
                  <th className="text-end font-semibold px-3 py-2">{t('payroll.stat_net')}</th>
                  <th className="text-end font-semibold px-3 py-2">{t('payroll.stat_employerBurden')}</th>
                </tr>
              </thead>
              <tbody className="num divide-y divide-ink-100">
                {s.breakdown.map((row) => (
                  <tr key={`${row.countryCode}-${row.currency}`} className="hover:bg-ink-50/40">
                    <td className="px-3 py-2 font-semibold text-ink-700">{row.currency}</td>
                    <td className="px-3 py-2 text-ink-500">{row.countryCode}</td>
                    <td className="px-3 py-2 text-end text-ink-700">{row.headcount}</td>
                    <td className="px-3 py-2 text-end text-ink-900">{fmtCents(row.grossInReportingCents, ccy)}</td>
                    <td className="px-3 py-2 text-end text-ink-900">{fmtCents(row.netInReportingCents, ccy)}</td>
                    <td className="px-3 py-2 text-end text-ink-900">{fmtCents(row.employerBurdenInReportingCents, ccy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {s.warnings.length > 0 && (
          <div className="mt-3 rounded-md border border-warn-500/30 bg-warn-50 px-3 py-2">
            <p className="text-xs font-semibold text-warn-700 mb-1">{t('payroll.summary_warnings', { defaultValue: 'Summary warnings' })}</p>
            <ul className="space-y-0.5">
              {s.warnings.map((w, i) => <li key={i} className="text-xs text-warn-600">{w}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Pre-run attendance view for JP employees: worked hours, overtime, and
 * estimated overtime pay for a chosen month — exactly what the JP run
 * folds into gross. A 36協定 violation badge flags anyone over the cap.
 */
function AttendanceForPeriodCard() {
  const { t } = useTranslation();
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const summary = usePayrollAttendanceSummary(year, month);

  const rows = summary.data?.employees ?? [];
  const anyOt = rows.some((r) => r.overtimeHours > 0);
  const anyViolation = rows.some((r) => r.violation);

  return (
    <div className="mt-4 rounded-2xl border border-ink-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 border-b border-ink-100 bg-ink-50 flex items-center justify-between gap-3 text-start"
      >
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-ink-700">{t('payroll.attendance_title', { defaultValue: 'Attendance this period' })}</h2>
          {anyViolation && <span className="text-[10px] font-semibold uppercase tracking-wide text-warn-700 bg-warn-50 border border-warn-500/30 rounded px-1.5 py-0.5">36協定</span>}
        </div>
        <span className="text-xs text-ink-500">{open ? '▴' : '▾'}</span>
      </button>

      {open && (
        <div className="px-5 py-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-1">{t('payroll.calcJpDialog_year')}</span>
              <input type="number" min="2024" max="2030" value={year} onChange={(e) => setYear(Number(e.target.value))} className="input w-24" />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-wide text-ink-500 font-semibold mb-1">{t('payroll.calcJpDialog_month')}</span>
              <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="input w-32">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{i + 1} ({m})</option>)}
              </select>
            </label>
            <p className="text-[11px] text-ink-500 flex-1 min-w-[12rem]">{t('payroll.attendance_hint', { defaultValue: 'Overtime here is folded into gross when you calculate the JP run for this month.' })}</p>
          </div>

          {summary.isLoading && <p className="mt-4 text-sm text-ink-500">{t('payroll.loading')}</p>}
          {!summary.isLoading && rows.length === 0 && (
            <p className="mt-4 text-sm text-ink-500">{t('payroll.attendance_none', { defaultValue: 'No JP employees with linked accounts. Link accounts under People → Edit to track attendance.' })}</p>
          )}
          {rows.length > 0 && (
            <div className="mt-4 rounded-lg border border-ink-100 overflow-hidden">
              <table className="min-w-full text-xs">
                <thead className="bg-ink-50 text-ink-600">
                  <tr>
                    <th className="text-start font-semibold px-3 py-2">{t('payroll.col_employee')}</th>
                    <th className="text-end font-semibold px-3 py-2">{t('payroll.attendance_days', { defaultValue: 'Days' })}</th>
                    <th className="text-end font-semibold px-3 py-2">{t('payroll.attendance_worked', { defaultValue: 'Worked h' })}</th>
                    <th className="text-end font-semibold px-3 py-2">{t('payroll.attendance_ot', { defaultValue: 'OT h' })}</th>
                    <th className="text-end font-semibold px-3 py-2">{t('payroll.attendance_otPay', { defaultValue: 'Est. OT pay' })}</th>
                    <th className="text-end font-semibold px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="num divide-y divide-ink-100">
                  {rows.map((r) => (
                    <tr key={r.employeeId} className={`hover:bg-ink-50/40 ${r.violation ? 'bg-warn-50/40' : ''}`}>
                      <td className="px-3 py-2 font-medium text-ink-900">
                        <Link to={`/app/people/${r.employeeId}`} className="hover:text-accent-600 hover:underline">{r.name}</Link>
                        {!r.linked && <span className="ms-2 text-[10px] text-ink-400">{t('payroll.attendance_unlinked', { defaultValue: '(no account)' })}</span>}
                      </td>
                      <td className="px-3 py-2 text-end text-ink-700">{r.daysWorked}</td>
                      <td className="px-3 py-2 text-end text-ink-700">{r.workedHours}</td>
                      <td className={`px-3 py-2 text-end font-semibold ${r.overtimeHours > 0 ? 'text-ink-900' : 'text-ink-400'}`}>{r.overtimeHours}</td>
                      <td className="px-3 py-2 text-end text-ink-900">{r.overtimePayCents > 0 ? fmtCents(r.overtimePayCents, 'JPY') : '—'}</td>
                      <td className="px-3 py-2 text-end">
                        {r.violation && <span className="text-[10px] font-semibold uppercase tracking-wide text-warn-700 bg-warn-50 border border-warn-500/30 rounded px-1.5 py-0.5">{t('payroll.attendance_over', { defaultValue: 'Over cap' })}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {anyOt && !anyViolation && (
            <p className="mt-3 text-[11px] text-ink-500">{t('payroll.attendance_otNote', { defaultValue: 'OT pay shown at base ÷ 160h × 1.25. Calculate the JP run to lock these into payslips.' })}</p>
          )}
        </div>
      )}
    </div>
  );
}

function currencyFor(country: string): string {
  switch (country) {
    case 'JP': return 'JPY';
    case 'US': return 'USD';
    case 'GB': return 'GBP';
    case 'DE': case 'FR': case 'ES': case 'IT': case 'NL': case 'IE': case 'BE': case 'PT': case 'AT': return 'EUR';
    case 'CA': return 'CAD'; case 'AU': return 'AUD'; case 'KR': return 'KRW';
    default: return 'USD';
  }
}

function RunLifecycleActions({ run }: { run: PayrollRun }) {
  const { t } = useTranslation();
  const members = useMembers();
  const createApproval = useCreateApproval();
  const markPaid = useMarkRunPaid();
  const close = useCloseRun();
  const [msg, setMsg] = useState<string | null>(null);

  async function requestApproval() {
    setMsg(null);
    const list = members.data?.members ?? [];
    const owner = list.find((m) => m.role === 'owner');
    const others = list.filter((m) => (m.role === 'owner' || m.role === 'admin') && !m.isYou).map((m) => m.id);
    const signers = others.length ? others : (owner ? [owner.id] : []);
    if (!signers.length) { setMsg(t('payroll.noApprover', { defaultValue: 'No owner/admin available to approve.' })); return; }
    try {
      await createApproval.mutateAsync({ targetKind: 'payroll_run', targetId: run.id, requiredSigners: signers });
      setMsg(t('payroll.approvalRequested', { defaultValue: 'Approval requested — track it under Approvals.' }));
    } catch {
      setMsg(t('payroll.approvalFailed', { defaultValue: 'Could not request approval.' }));
    }
  }

  const btn = 'inline-flex h-8 items-center px-3 rounded-lg text-xs font-semibold transition disabled:opacity-50';
  return (
    <div className="px-6 py-3 border-b border-ink-100 bg-ink-50/50 flex flex-wrap items-center gap-2">
      {run.status === 'calculated' && (
        <button onClick={requestApproval} disabled={createApproval.isPending} className={`${btn} bg-accent-500 text-white hover:bg-accent-600`}>
          {t('payroll.requestApproval', { defaultValue: 'Request approval' })}
        </button>
      )}
      {run.status === 'approved' && (
        <button onClick={() => markPaid.mutate(run.id)} disabled={markPaid.isPending} className={`${btn} bg-accent-500 text-white hover:bg-accent-600`}>
          {t('payroll.markPaid', { defaultValue: 'Mark paid' })}
        </button>
      )}
      {run.status === 'paid' && (
        <button onClick={() => close.mutate(run.id)} disabled={close.isPending} className={`${btn} border border-ink-300 text-ink-700 hover:bg-ink-100`}>
          {t('payroll.closeRun', { defaultValue: 'Close run' })}
        </button>
      )}
      {run.status === 'closed' && <span className="text-xs text-ink-500">{t('payroll.runClosed', { defaultValue: 'Run closed.' })}</span>}
      {run.status === 'draft' && <span className="text-xs text-ink-500">{t('payroll.runDraft', { defaultValue: 'Draft — calculate to continue.' })}</span>}
      {msg && <span className="text-xs text-emerald-600">{msg}</span>}
    </div>
  );
}

function RunDetail({ run, lines }: { run: PayrollRun; lines: PayrollLine[] }) {
  const { t } = useTranslation();
  const isJp = run.countryCode === 'JP';
  const isUs = run.countryCode === 'US';
  const isUk = run.countryCode === 'GB';
  const isDe = run.countryCode === 'DE';
  const ccy = currencyFor(run.countryCode);

  return (
    <div className="rounded-2xl border border-ink-200 bg-white overflow-hidden">
      <div className="px-6 py-4 border-b border-ink-100 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-ink-900">{run.countryCode} · {run.periodYear}/{String(run.periodMonth).padStart(2,'0')} · {run.runType}</h3>
          <p className="text-xs text-ink-500">{run.status.toUpperCase()} · {lines.length} payslip{lines.length === 1 ? '' : 's'}</p>
        </div>
        {run.totals && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-end">
            <Stat label={t('payroll.stat_gross')} value={fmtCents(run.totals.gross_cents, ccy)} />
            <Stat label={t('payroll.stat_net')} value={fmtCents(run.totals.net_cents, ccy)} />
            <Stat label={t('payroll.stat_employerBurden')} value={fmtCents(run.totals.employer_burden_cents, ccy)} />
            <Stat label={t('payroll.stat_cashOutlay')} value={fmtCents(run.totals.total_employer_cash_cents, ccy)} />
          </div>
        )}
      </div>

      <RunLifecycleActions run={run} />

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-ink-50 text-ink-600">
            <tr>
              <th className="text-start font-semibold px-3 py-2 sticky start-0 bg-ink-50">{t('payroll.col_employee')}</th>
              <th className="text-end font-semibold px-3 py-2">{t('payroll.col_gross')}</th>
              {isJp && <>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_kenpo')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_kaigo')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_kounen')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_koyou')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_gensen')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_juuminzei')}</th>
              </>}
              {isUs && <>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_federal')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_state')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_fica')}</th>
              </>}
              {isUk && <>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_paye')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_ni')}</th>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_pension')}</th>
              </>}
              {isDe && <>
                <th className="text-end font-semibold px-3 py-2">{t('payroll.col_lohnsteuer')}</th>
              </>}
              <th className="text-end font-semibold px-3 py-2 bg-accent-50 text-accent-700">{isJp ? t('payroll.col_net') : t('payroll.stat_net')}</th>
              <th className="text-end font-semibold px-3 py-2">{t('payroll.col_pdf')}</th>
            </tr>
          </thead>
          <tbody className="num">
            {lines.map((l) => (
              <tr key={l.id} className="border-t border-ink-100 hover:bg-ink-50/30">
                <td className="px-3 py-2 sticky start-0 bg-inherit font-mono text-[10px]">
                  <Link to={`/app/people/${l.employeeId}`} className="text-accent-700 hover:text-accent-600 hover:underline">{l.employeeId.slice(0, 8)}…</Link>
                </td>
                <td className="px-3 py-2 text-end text-ink-900">{fmtCents(l.grossCents, ccy)}</td>
                {isJp && <>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.healthInsuranceEmployeeCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-500">{fmtCents(l.longTermCareEmployeeCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.pensionEmployeeCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.employmentInsuranceEmployeeCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.incomeTaxCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.residentTaxCents, ccy)}</td>
                </>}
                {isUs && <>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.federalTaxCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.stateTaxCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.ficaEmployeeCents, ccy)}</td>
                </>}
                {isUk && <>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.incomeTaxCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(l.niEmployeeCents, ccy)}</td>
                  <td className="px-3 py-2 text-end text-ink-500">{fmtCents(((l.breakdown as Record<string, number | null>)?.pension_employee_cents) ?? null, ccy)}</td>
                </>}
                {isDe && <>
                  <td className="px-3 py-2 text-end text-ink-700">{fmtCents(((l.breakdown as Record<string, number | null>)?.lohnsteuer_cents) ?? null, ccy)}</td>
                </>}
                <td className="px-3 py-2 text-end font-semibold text-accent-700 bg-accent-50/50">{fmtCents(l.netCents, ccy)}</td>
                <td className="px-3 py-2 text-end">
                  <a href={payslipPdfUrl(l.id)} target="_blank" rel="noopener" className="text-accent-700 hover:text-accent-600 text-[10px] underline font-semibold">PDF</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-3 border-t border-ink-100 text-[11px] text-ink-500 bg-ink-50/50">
        {isJp && t('payroll.ratesNoteJp')}
        {isUs && t('payroll.ratesNoteUs')}
        {isUk && t('payroll.ratesNoteUk')}
        {isDe && t('payroll.ratesNoteDe')}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-ink-500 font-semibold">{label}</p>
      <p className="num text-sm font-semibold text-ink-900">{value}</p>
    </div>
  );
}

// ───────────────────────── Dialogs ─────────────────────────

function DialogShell({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-glow border border-ink-200 max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto"
        onSubmit={(e) => e.preventDefault()}>
        {children}
      </form>
    </div>
  );
}

function PeriodFields({ year, setYear, month, setMonth, payDate, setPayDate }: {
  year: number; setYear: (n: number) => void;
  month: number; setMonth: (n: number) => void;
  payDate: string; setPayDate: (s: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-3 gap-3 mt-5">
      <Field label={t('payroll.calcJpDialog_year')}><input type="number" min="2024" max="2030" value={year} onChange={(e) => setYear(Number(e.target.value))} className="input" /></Field>
      <Field label={t('payroll.calcJpDialog_month')}>
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="input">
          {MONTHS.map((m, i) => <option key={i} value={i + 1}>{i + 1} ({m})</option>)}
        </select>
      </Field>
      <Field label={t('payroll.calcJpDialog_payDate')}><input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="input" /></Field>
    </div>
  );
}

function CalcJpDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: (id: string, warnings: string[]) => void }) {
  const { t } = useTranslation();
  const employees = useEmployees({ country: 'JP' });
  const calc = useCalculateJpRun();
  const eligible = useMemo(() => (employees.data ?? []).filter((e) => e.baseSalaryCents != null && e.status !== 'terminated'), [employees.data]);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [payDate, setPayDate] = useState<string>(() => new Date(today.getFullYear(), today.getMonth(), 25).toISOString().slice(0, 10));
  const [residentTaxes, setResidentTaxes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const perEmployee = Object.fromEntries(Object.entries(residentTaxes).filter(([, v]) => v && Number(v) > 0).map(([id, v]) => [id, { residentTaxCentsMonthly: Math.round(Number(v) * 100) }]));
      const r = await calc.mutateAsync({ periodYear: year, periodMonth: month, payDate, perEmployee });
      onSuccess(r.run.id, r.warnings ?? []);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <h2 className="text-lg font-semibold text-ink-900">{t('payroll.calcJpDialog_title')}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('payroll.calcJpDialog_subtitle', { count: eligible.length })}</p>
      <PeriodFields year={year} setYear={setYear} month={month} setMonth={setMonth} payDate={payDate} setPayDate={setPayDate} />
      <div className="mt-5 rounded-lg border border-ink-200 overflow-hidden">
        <div className="px-3 py-2 bg-ink-50 text-xs font-semibold text-ink-700">{t('payroll.calcJpDialog_residentTaxTitle')}</div>
        <table className="min-w-full text-xs">
          <tbody className="divide-y divide-ink-100">
            {eligible.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2">
                  <p className="text-ink-900 font-medium">{e.legalNameLocal}</p>
                  <p className="text-[10px] text-ink-500 num">{t('payroll.calcJpDialog_baseLabel')} {fmtCents(e.baseSalaryCents, 'JPY')}</p>
                </td>
                <td className="px-3 py-2 text-end">
                  <input type="number" min="0" step="100" placeholder="0" value={residentTaxes[e.id] ?? ''} onChange={(ev) => setResidentTaxes({ ...residentTaxes, [e.id]: ev.target.value })} className="input w-32 text-end num" />
                </td>
              </tr>
            ))}
            {eligible.length === 0 && <tr><td className="px-3 py-3 text-ink-500">{t('payroll.calcJpDialog_noJp')}</td></tr>}
          </tbody>
        </table>
      </div>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <DialogActions onClose={onClose} onSubmit={submit} disabled={calc.isPending || eligible.length === 0} primaryLabel={t('payroll.calcJpDialog_calculate', { count: eligible.length })} primaryBusyLabel={t('payroll.calcJpDialog_calculating')} busy={calc.isPending} />
    </DialogShell>
  );
}

function CalcJpBonusDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: (id: string, warnings: string[]) => void }) {
  const { t } = useTranslation();
  const employees = useEmployees({ country: 'JP' });
  const calc = useCalculateJpBonus();
  const eligible = useMemo(() => (employees.data ?? []).filter((e) => e.status !== 'terminated'), [employees.data]);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [payDate, setPayDate] = useState<string>(() => today.toISOString().slice(0, 10));
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    const perEmployee: Record<string, { bonusCents: number }> = {};
    for (const [id, v] of Object.entries(amounts)) {
      const n = Number(v);
      if (v && n > 0) perEmployee[id] = { bonusCents: Math.round(n * 100) };
    }
    if (Object.keys(perEmployee).length === 0) { setError(t('payroll.calcJpBonus_enterAmount', { defaultValue: 'Enter at least one bonus amount.' })); return; }
    try {
      const r = await calc.mutateAsync({ periodYear: year, periodMonth: month, payDate, perEmployee });
      onSuccess(r.run.id, r.warnings ?? []);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <h2 className="text-lg font-semibold text-ink-900">{t('payroll.calcJpBonus_title')}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('payroll.calcJpBonus_subtitle')}</p>
      <PeriodFields year={year} setYear={setYear} month={month} setMonth={setMonth} payDate={payDate} setPayDate={setPayDate} />
      <div className="mt-5 rounded-lg border border-ink-200 overflow-hidden">
        <div className="px-3 py-2 bg-ink-50 text-xs font-semibold text-ink-700">{t('payroll.calcJpBonus_bonusAmount')}</div>
        <table className="min-w-full text-xs">
          <tbody className="divide-y divide-ink-100">
            {eligible.map((e) => (
              <tr key={e.id}>
                <td className="px-3 py-2"><p className="text-ink-900 font-medium">{e.legalNameLocal}</p></td>
                <td className="px-3 py-2 text-end"><input type="number" min="0" step="10000" placeholder="0" value={amounts[e.id] ?? ''} onChange={(ev) => setAmounts({ ...amounts, [e.id]: ev.target.value })} className="input w-40 text-end num" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <DialogActions onClose={onClose} onSubmit={submit} disabled={calc.isPending} primaryLabel={t('payroll.calcJpBonus_calculate')} primaryBusyLabel={t('payroll.calcGeneric_calculating')} busy={calc.isPending} />
    </DialogShell>
  );
}

function CalcUsDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: (id: string, warnings: string[]) => void }) {
  const { t } = useTranslation();
  const employees = useEmployees({ country: 'US' });
  const calc = useCalculateUsRun();
  const eligible = useMemo(() => (employees.data ?? []).filter((e) => e.baseSalaryCents != null && e.status !== 'terminated'), [employees.data]);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [payDate, setPayDate] = useState<string>(() => today.toISOString().slice(0, 10));
  const [payPeriods, setPayPeriods] = useState<12 | 24 | 26 | 52>(26);
  const [perEmp, setPerEmp] = useState<Record<string, { filingStatus?: 'single' | 'married_jointly' | 'head_of_household'; stateCode?: string; allowances?: number }>>({});
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const r = await calc.mutateAsync({ periodYear: year, periodMonth: month, payDate, payPeriodsPerYear: payPeriods, perEmployee: perEmp });
      onSuccess(r.run.id, r.warnings ?? []);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <h2 className="text-lg font-semibold text-ink-900">{t('payroll.calcUs_title')}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('payroll.calcUs_subtitle', { count: eligible.length })}</p>
      <PeriodFields year={year} setYear={setYear} month={month} setMonth={setMonth} payDate={payDate} setPayDate={setPayDate} />
      <div className="mt-3">
        <Field label={t('payroll.calcUs_payPeriods')}>
          <select value={payPeriods} onChange={(e) => setPayPeriods(Number(e.target.value) as 12 | 24 | 26 | 52)} className="input">
            <option value={12}>12 (monthly)</option><option value={24}>24 (semi-monthly)</option><option value={26}>26 (biweekly)</option><option value={52}>52 (weekly)</option>
          </select>
        </Field>
      </div>
      <div className="mt-5 rounded-lg border border-ink-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-ink-50"><tr>
            <th className="px-3 py-2 text-start">{t('payroll.col_employee')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcUs_filingStatus')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcUs_stateCode')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcUs_allowances')}</th>
          </tr></thead>
          <tbody className="divide-y divide-ink-100">
            {eligible.map((e) => {
              const cur = perEmp[e.id] ?? {};
              const upd = (patch: Partial<typeof cur>) => setPerEmp({ ...perEmp, [e.id]: { ...cur, ...patch } });
              return (
                <tr key={e.id}>
                  <td className="px-3 py-2 text-ink-900 font-medium">{e.legalNameLocal}</td>
                  <td className="px-3 py-2"><select value={cur.filingStatus ?? 'single'} onChange={(ev) => upd({ filingStatus: ev.target.value as 'single' | 'married_jointly' | 'head_of_household' })} className="input">
                    <option value="single">{t('payroll.filingStatus_single')}</option>
                    <option value="married_jointly">{t('payroll.filingStatus_married_jointly')}</option>
                    <option value="head_of_household">{t('payroll.filingStatus_head_of_household')}</option>
                  </select></td>
                  <td className="px-3 py-2">
                    <select
                      value={cur.stateCode ?? 'CA'}
                      onChange={(ev) => upd({ stateCode: ev.target.value })}
                      className="input w-44"
                    >
                      {US_STATES.map((s) => (
                        <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2"><input type="number" min={0} value={cur.allowances ?? 0} onChange={(ev) => upd({ allowances: Number(ev.target.value) })} className="input w-16" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <DialogActions onClose={onClose} onSubmit={submit} disabled={calc.isPending || eligible.length === 0} primaryLabel={t('payroll.calcGeneric_calculate')} primaryBusyLabel={t('payroll.calcGeneric_calculating')} busy={calc.isPending} />
    </DialogShell>
  );
}

/**
 * Datalist of common UK tax codes — pinned at module scope so all
 * <input list="uk-tax-codes"> elements pick it up automatically.
 * Real-world codes are open-ended (K-codes, X markers, Welsh/Scottish
 * prefixes); the input still accepts free text.
 */
function UkTaxCodeDatalist() {
  return (
    <datalist id="uk-tax-codes">
      {UK_COMMON_TAX_CODES.map((code) => <option key={code} value={code} />)}
    </datalist>
  );
}

function CalcUkDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: (id: string, warnings: string[]) => void }) {
  const { t } = useTranslation();
  const employees = useEmployees({ country: 'GB' });
  const calc = useCalculateUkRun();
  const eligible = useMemo(() => (employees.data ?? []).filter((e) => e.baseSalaryCents != null && e.status !== 'terminated'), [employees.data]);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [payDate, setPayDate] = useState<string>(() => today.toISOString().slice(0, 10));
  const [payPeriods, setPayPeriods] = useState<12 | 26 | 52>(12);
  const [perEmp, setPerEmp] = useState<Record<string, { taxCode?: string; pensionContributionPct?: number }>>({});
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const r = await calc.mutateAsync({ periodYear: year, periodMonth: month, payDate, payPeriodsPerYear: payPeriods, perEmployee: perEmp });
      onSuccess(r.run.id, r.warnings ?? []);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <UkTaxCodeDatalist />
      <h2 className="text-lg font-semibold text-ink-900">{t('payroll.calcUk_title')}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('payroll.calcUk_subtitle', { count: eligible.length })}</p>
      <PeriodFields year={year} setYear={setYear} month={month} setMonth={setMonth} payDate={payDate} setPayDate={setPayDate} />
      <div className="mt-3">
        <Field label={t('payroll.calcUs_payPeriods')}>
          <select value={payPeriods} onChange={(e) => setPayPeriods(Number(e.target.value) as 12 | 26 | 52)} className="input">
            <option value={12}>12 (monthly)</option><option value={26}>26 (biweekly)</option><option value={52}>52 (weekly)</option>
          </select>
        </Field>
      </div>
      <div className="mt-5 rounded-lg border border-ink-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-ink-50"><tr><th className="px-3 py-2 text-start">{t('payroll.col_employee')}</th><th className="px-3 py-2 text-start">{t('payroll.calcUk_taxCode')}</th><th className="px-3 py-2 text-start">{t('payroll.calcUk_pensionPct')}</th></tr></thead>
          <tbody className="divide-y divide-ink-100">
            {eligible.map((e) => {
              const cur = perEmp[e.id] ?? {};
              const upd = (patch: Partial<typeof cur>) => setPerEmp({ ...perEmp, [e.id]: { ...cur, ...patch } });
              return (
                <tr key={e.id}>
                  <td className="px-3 py-2 text-ink-900 font-medium">{e.legalNameLocal}</td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      list="uk-tax-codes"
                      value={cur.taxCode ?? '1257L'}
                      onChange={(ev) => upd({ taxCode: ev.target.value.toUpperCase() })}
                      className="input w-28"
                      placeholder="1257L"
                    />
                  </td>
                  <td className="px-3 py-2"><input type="number" step="0.01" min="0" max="1" value={cur.pensionContributionPct ?? 0.05} onChange={(ev) => upd({ pensionContributionPct: Number(ev.target.value) })} className="input w-20" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <DialogActions onClose={onClose} onSubmit={submit} disabled={calc.isPending || eligible.length === 0} primaryLabel={t('payroll.calcGeneric_calculate')} primaryBusyLabel={t('payroll.calcGeneric_calculating')} busy={calc.isPending} />
    </DialogShell>
  );
}

function CalcDeDialog({ onClose, onSuccess }: { onClose: () => void; onSuccess: (id: string, warnings: string[]) => void }) {
  const { t } = useTranslation();
  const employees = useEmployees({ country: 'DE' });
  const calc = useCalculateDeRun();
  const eligible = useMemo(() => (employees.data ?? []).filter((e) => e.baseSalaryCents != null && e.status !== 'terminated'), [employees.data]);
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [payDate, setPayDate] = useState<string>(() => today.toISOString().slice(0, 10));
  const [payPeriods, setPayPeriods] = useState<12 | 24 | 26 | 52>(12);
  const [perEmp, setPerEmp] = useState<Record<string, { taxClass?: 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI'; children?: number; churchTax?: boolean; bundesland?: 'BY' | 'BW' | 'OTHER' }>>({});
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      const r = await calc.mutateAsync({ periodYear: year, periodMonth: month, payDate, payPeriodsPerYear: payPeriods, perEmployee: perEmp });
      onSuccess(r.run.id, r.warnings ?? []);
    } catch (err: unknown) {
      setError(((err as { response?: { data?: { message?: string } } })?.response?.data?.message) || (err as Error).message);
    }
  }

  return (
    <DialogShell onClose={onClose}>
      <h2 className="text-lg font-semibold text-ink-900">{t('payroll.calcDe_title')}</h2>
      <p className="mt-1 text-xs text-ink-500">{t('payroll.calcDe_subtitle', { count: eligible.length })}</p>
      <PeriodFields year={year} setYear={setYear} month={month} setMonth={setMonth} payDate={payDate} setPayDate={setPayDate} />
      <div className="mt-3">
        <Field label={t('payroll.calcUs_payPeriods')}>
          <select value={payPeriods} onChange={(e) => setPayPeriods(Number(e.target.value) as 12 | 24 | 26 | 52)} className="input">
            <option value={12}>12</option><option value={24}>24</option><option value={26}>26</option><option value={52}>52</option>
          </select>
        </Field>
      </div>
      <div className="mt-5 rounded-lg border border-ink-200 overflow-hidden">
        <table className="min-w-full text-xs">
          <thead className="bg-ink-50"><tr>
            <th className="px-3 py-2 text-start">{t('payroll.col_employee')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcDe_taxClass')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcDe_children')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcDe_churchTax')}</th>
            <th className="px-3 py-2 text-start">{t('payroll.calcDe_bundesland')}</th>
          </tr></thead>
          <tbody className="divide-y divide-ink-100">
            {eligible.map((e) => {
              const cur = perEmp[e.id] ?? {};
              const upd = (patch: Partial<typeof cur>) => setPerEmp({ ...perEmp, [e.id]: { ...cur, ...patch } });
              return (
                <tr key={e.id}>
                  <td className="px-3 py-2 text-ink-900 font-medium">{e.legalNameLocal}</td>
                  <td className="px-3 py-2"><select value={cur.taxClass ?? 'I'} onChange={(ev) => upd({ taxClass: ev.target.value as 'I' | 'II' | 'III' | 'IV' | 'V' | 'VI' })} className="input">
                    <option value="I">I</option><option value="II">II</option><option value="III">III</option><option value="IV">IV</option><option value="V">V</option><option value="VI">VI</option>
                  </select></td>
                  <td className="px-3 py-2"><input type="number" min={0} value={cur.children ?? 0} onChange={(ev) => upd({ children: Number(ev.target.value) })} className="input w-16" /></td>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={cur.churchTax ?? false} onChange={(ev) => upd({ churchTax: ev.target.checked })} className="accent-accent-500" /></td>
                  <td className="px-3 py-2"><select value={cur.bundesland ?? 'OTHER'} onChange={(ev) => upd({ bundesland: ev.target.value as 'BY' | 'BW' | 'OTHER' })} className="input">
                    <option value="OTHER">Other (9%)</option><option value="BY">BY (8%)</option><option value="BW">BW (8%)</option>
                  </select></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {error && <div className="mt-4 rounded-md bg-warn-50 border border-warn-500/30 text-warn-600 text-sm px-3 py-2">{error}</div>}
      <DialogActions onClose={onClose} onSubmit={submit} disabled={calc.isPending || eligible.length === 0} primaryLabel={t('payroll.calcGeneric_calculate')} primaryBusyLabel={t('payroll.calcGeneric_calculating')} busy={calc.isPending} />
    </DialogShell>
  );
}

function DialogActions({ onClose, onSubmit, disabled, primaryLabel, primaryBusyLabel, busy }: { onClose: () => void; onSubmit: () => void; disabled: boolean; primaryLabel: string; primaryBusyLabel: string; busy: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex justify-end gap-2 pt-5">
      <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-100 rounded-lg transition">{t('common.cancel')}</button>
      <button type="button" onClick={onSubmit} disabled={disabled} className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-50 transition">{busy ? primaryBusyLabel : primaryLabel}</button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-ink-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
